import React from 'react';
import clsx from 'clsx';

import Button from '../Button';
import { Input } from '../Input';
import Validator from './components/Validator';
import { usePermissionAllowed } from '../../hooks';
import { useEditorDnD } from './hooks/useEditorDnD';
import { SearchIcon } from '../SearchBar/SearchIcon';
import VariablesTabs from './components/VariablesTabs';
import { usePromptPreview } from './hooks/usePromptPreview';
import { checkPromptFormattingErrors } from '../../utilities';
import { PERMISSIONS } from '../../shared/constants/permissions';
import { useTextSearchReplace } from './hooks/useTextSearchReplace';
import { ReplaceIcon, ArrowRightIcon, ArrowLeftIcon } from '../../assets/icons';

export type TextSearchReplaceProps = {
  editorRef: React.RefObject<HTMLDivElement>;
  initialContent: string;
  onContentChange?: (content: string) => void;
  isLibraryMode?: boolean;
};

export const TextSearchReplace: React.FC<TextSearchReplaceProps> = ({
  editorRef,
  initialContent,
  onContentChange,
  isLibraryMode = true,
}) => {
  const isPromptLibraryAccess = usePermissionAllowed(
    PERMISSIONS.PROMPT_LIBRARY_ACCESS
  );

  const {
    handlePreviewPrompt,
    previewingPrompt,
    previewPrompt,
    isPreviewMode,
    setIsPreviewMode,
    setPreviewPrompt,
    currentContent,
    setCurrentContent,
  } = usePromptPreview(initialContent);

  const errors = checkPromptFormattingErrors(currentContent);

  const {
    findText,
    setFindText,
    replaceText,
    setReplaceText,
    handleHighlight,
    handleReplace,
  } = useTextSearchReplace({
    editorRef,
    initialContent: currentContent,
    onContentChange: content => {
      setCurrentContent(content);
      onContentChange?.(content);
    },
    previewPrompt,
    isPreviewMode,
  });

  const {
    showVariables,
    setShowVariables,
    handleEditorDragOver,
    handleEditorDrop,
  } = useEditorDnD({
    editorRef,
    onContentChange: content => {
      setCurrentContent(content);
      onContentChange?.(content);
    },
    isPreviewMode,
  });

  const handlePreview = () => {
    setIsPreviewMode(true);
    handlePreviewPrompt(currentContent);
    setPreviewPrompt('Preview prompt will appear here...');
  };

  return (
    <div className="flex flex-col gap-y-2">
      <div className="flex gap-2">
        <Input
          value={findText}
          placeholder="Find..."
          onChange={value => setFindText(value)}
        />
        <Input
          value={replaceText}
          placeholder="Replace with..."
          onChange={value => setReplaceText(value)}
        />
      </div>

      <div className="flex justify-between items-center py-2">
        {isLibraryMode && (
          <div className="flex justify-end items-center gap-x-2 mb-2 h-full">
            <Button
              size="small"
              type="empty"
              label="Prompt"
              className={`text-mainText border border-border rounded-full ${
                !isPreviewMode ? 'bg-select' : 'bg-gray-100'
              }`}
              onClick={() => {
                setIsPreviewMode(false);
              }}
            />
            {isPromptLibraryAccess && (
              <Button
                size="small"
                label="Preview"
                type="contrast"
                className={`text-mainText border border-border rounded-full ${
                  isPreviewMode ? 'bg-select' : 'bg-gray-100'
                }`}
                loading={previewingPrompt}
                onClick={handlePreview}
                disabled={!currentContent}
              />
            )}
            <Validator errors={errors} editorRef={editorRef} />
          </div>
        )}
        <div className="flex gap-sm justify-end">
          <Button
            type="light"
            label="Search"
            className="text-secondary border border-border"
            icon={<SearchIcon className="text-main" width={14} height={14} />}
            onClick={handleHighlight}
            size="small"
          />
          <Button
            type="light"
            label="Replace"
            className="text-white bg-secondary py-0"
            icon={<ReplaceIcon width={16} height={16} color="white" />}
            onClick={handleReplace}
            size="small"
          />
          {isLibraryMode && (
            <div
              onClick={() => setShowVariables(!showVariables)}
              className="flex items-center gap-1 px-3 py-2 text-mainText rounded-xl text-tiny cursor-pointer hover:bg-gray-100 transition-colors"
            >
              {showVariables ? (
                <p className="flex items-center gap-2">
                  <ArrowRightIcon />
                  <span>Hide Variables</span>
                </p>
              ) : (
                <p className="flex items-center gap-2">
                  <ArrowLeftIcon />
                  <span>Show Variables</span>
                </p>
              )}{' '}
            </div>
          )}
        </div>
      </div>

      <div
        className="flex gap-6 w-full"
        style={{
          height: '400px',
        }}
      >
        {isPreviewMode ? (
          <div
            key="preview"
            className={`flex-grow border border-gray-300 rounded-xl overflow-y-auto p-3 text-medium bg-gray-100 text-black whitespace-pre-wrap min-h-0`}
          >
            {previewPrompt || 'Preview prompt will appear here...'}
          </div>
        ) : (
          <div
            key="editor"
            ref={node => {
              if (editorRef && node) editorRef.current = node;
            }}
            contentEditable
            suppressContentEditableWarning
            onDragOver={handleEditorDragOver}
            onDrop={handleEditorDrop}
            className={clsx(
              errors.length ? 'border-danger' : 'border-gray-300',
              `flex-grow border border-solid outline-none rounded-xl overflow-y-auto p-3 text-medium bg-white text-black whitespace-pre-wrap`
            )}
          />
        )}
        {showVariables && (
          <div className="w-1/3 border border-gray-300 rounded-xl p-3 text-medium bg-gray-100 text-black flex flex-col">
            <VariablesTabs
              isPreviewMode={isPreviewMode}
              isPromptLibraryAccess={isPromptLibraryAccess}
            />
          </div>
        )}
      </div>
    </div>
  );
};
